import { Controller, useForm } from 'react-hook-form';
import _ from 'lodash';
import Link from '@fuse/core/Link';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
// Image - use regular img tag
import { useForgotPasswordMutation } from '@/store/services/auth';
import { toast } from 'react-toastify';
import { CircularProgress, TextField, Typography, Paper, Button } from '@mui/material';

/**
 * Form Validation Schema
 */
const schema = z.object({
	email: z.string().email('You must enter a valid email').nonempty('You must enter an email')
});

type FormType = {
	email: string;
};

const defaultValues = {
	email: ''
};

/**
 * THe classic forgot password page.
 */
function ClassicForgotPasswordPage() {
	const navigate = useNavigate();
	const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
	const { control, formState, handleSubmit, reset } = useForm({
		mode: 'onChange',
		defaultValues,
		resolver: zodResolver(schema)
	});

	const { isValid, dirtyFields, errors } = formState;

	async function onSubmit(formData: FormType) {
		const { email } = formData;
		const result = await forgotPassword({ email });

		if (!result.error) {
			navigate(`/confirm-password?email=${email}`);
			reset(defaultValues);
		} else {
			const error = result.error as any;
			const errorData = error?.data;
			toast.error(errorData?.errors?.[0]?.detail || 'Something went wrong');
		}
	}

	return (
		<div className="flex min-w-0 flex-auto flex-col items-center sm:justify-center">
			<Paper className="bg-white min-h-full w-full rounded-none px-4 py-8 sm:min-h-auto sm:w-auto sm:rounded-xl sm:p-12 sm:shadow-sm">
				<div className="mx-auto w-full max-w-80 sm:mx-0 sm:w-80 flex flex-col items-center">
					<img
						src="/assets/images/logo/logo_loanapp.png"
						alt="ASSETS ID"
						width={165}
						height={70}
					/>

					<div className='w-full'>

					<Typography className="mt-8 text-4xl font-extrabold leading-[1.25] tracking-tight">
						Forgot password?
					</Typography>
					<div className="mt-0.5 flex items-baseline font-medium">
						<Typography>Enter email to reset your password.</Typography>
					</div>

					<form
						name="registerForm"
						noValidate
						className="mt-8 flex w-full flex-col justify-center"
						onSubmit={handleSubmit(onSubmit)}
					>
						<Controller
							name="email"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									className="mb-6"
									label="Email"
									type="email"
									error={!!errors.email}
									helperText={errors?.email?.message}
									variant="outlined"
									required
									fullWidth
								/>
							)}
						/>

						<Button
							variant="contained"
							color="secondary"
							className=" mt-1 w-full"
							aria-label="Register"
							disabled={isLoading || _.isEmpty(dirtyFields) || !isValid}
							type="submit"
							size="large"
							startIcon={isLoading && <CircularProgress size={20} />}
						>
							Send reset link
						</Button>

						<Typography
							className="mt-8 text-md font-medium"
							color="text.secondary"
						>
							<span>Return to</span>
							<Link
								className="ml-1"
								to="/sign-in"
							>
								sign in
							</Link>
						</Typography>
					</form>
					</div>
				</div>
			</Paper>
		</div>
	);
}

export default ClassicForgotPasswordPage;
